import { useIsFetching, useIsMutating, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  apiCustomerAddressAuditsFormatGetCollectionInfiniteQueryKey,
  apiCustomersIdFormatGetOptions,
} from "@/generated/api/@tanstack/react-query.gen.ts";
import { Separator } from "@/components/ui/separator.tsx";
import { PageErrorNotFound } from "@/components/page-error.tsx";
import CustomerBillingInformationSection from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-billing-section.tsx";
import { CustomerSectionHeader } from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-header-section.tsx";
import CustomerDetailHistorySection from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-history-section.tsx";
import CustomerInformationSection from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-info-section.tsx";
import CustomerShippingInformationSection from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-shipping-section.tsx";
import CustomerDetailSkeleton from "@/app/(authenticated)/$merchantId/customers/$id/_components/customer-skeleton.tsx";
import AddBillingAddressSheet from "@/app/(authenticated)/$merchantId/customers/(add-update-billing)/_components/add-billing-address-sheet.tsx";
import ShippingAddressSheet from "@/app/(authenticated)/$merchantId/customers/(add-update-shipping)/_components/add-shipping-address-sheet.tsx";
import UpdateCustomerSheet from "@/app/(authenticated)/$merchantId/customers/(update-customer)/_components/update-customer-sheet.tsx";
import {
  CUSTOMER_DETAIL_STALE_TIME,
  mutationUpdateBillingKey,
  mutationUpdateCustomerInformationKey,
  mutationUpdateShippingKey,
} from "@/app/(authenticated)/$merchantId/customers/_data/use-refetch-detail.ts";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/customers/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const mid = useMerchantProvider().selectedMerchant?.merchAccountId;
  const id = Route.useParams().id;

  const isUpdatingShipping = useIsMutating({
    mutationKey: [mutationUpdateShippingKey],
  });
  const isUpdatingBilling = useIsMutating({
    mutationKey: [mutationUpdateBillingKey],
  });
  const isUpdatingCustomer = useIsMutating({
    mutationKey: [mutationUpdateCustomerInformationKey],
  });

  const isFetchingCustomerHistory = useIsFetching({
    queryKey: apiCustomerAddressAuditsFormatGetCollectionInfiniteQueryKey({
      headers: {
        "X-Merchant-Account-Id": mid ?? "",
      },
    }),
  });

  const { data, isLoading, error, refetch } = useQuery({
    ...apiCustomersIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": mid ?? "",
      },
      path: { id: id },
    }),
    enabled: !!mid,
    staleTime: CUSTOMER_DETAIL_STALE_TIME,
  });

  if (isLoading || !mid) {
    return <CustomerDetailSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <PageErrorNotFound message={error?.message || "Customer not found"} />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <CustomerSectionHeader
        title="Customer Information"
        isChanging={Boolean(isUpdatingCustomer) || isLoading}
        action={
          data && (
            <UpdateCustomerSheet
              data={data}
              onUpdateSuccess={() => {
                refetch();
              }}
            />
          )
        }
      />
      <CustomerInformationSection data={data} />

      <Separator />

      <CustomerSectionHeader
        title="Billing Information"
        isChanging={Boolean(isUpdatingBilling) || isLoading}
        action={
          data["@id"] &&
          data.id && (
            <AddBillingAddressSheet
              customerResourceId={data["@id"]}
              customerId={data.id}
            />
          )
        }
      />
      {data.id && data["@id"] && (
        <CustomerBillingInformationSection
          customerId={data.id}
          customerResourceId={data["@id"]}
          billings={data.listBillingAddress ?? []}
        />
      )}

      <CustomerSectionHeader
        title="Shipping Information"
        isChanging={Boolean(isUpdatingShipping) || isLoading}
        action={
          data["@id"] &&
          data.id && (
            <ShippingAddressSheet
              customerResourceId={data["@id"]}
              customerId={data.id}
            />
          )
        }
      />

      {data.id && data["@id"] && (
        <CustomerShippingInformationSection
          customerResourceId={data["@id"]}
          customerId={data.id}
          shippings={data.listShippingAddress ?? []}
        />
      )}

      <CustomerSectionHeader
        title="Customer History"
        isChanging={Boolean(isFetchingCustomerHistory)}
      />
      {data.id && <CustomerDetailHistorySection customerId={data.id} />}
    </div>
  );
}
